import { diasTabelaHorario } from '@/templates/Cadastro/TabelaHorario/constants/dias-tabela-horario'
import { PreviewEspelho, zod } from '@acme/core'
import { z } from 'zod'
import ApiService from '../api.service'
import { Funcionario } from './funcionario'

const horariosSchema = z
    .object({
        label: z.string().optional(),
        Ent1: z.string().nullish(),
        Ent2: z.string().nullish(),
        Ent3: z.string().nullish(),
        Sai1: z.string().nullish(),
        Sai2: z.string().nullish(),
        Sai3: z.string().nullish(),
        Tot: z.string().nullish(),
    })
    .refine(
        data => {
            const { Ent1, Ent2, Ent3, Sai1, Sai2, Sai3 } = data
            const entradas = [Ent1, Ent2, Ent3].filter(Boolean)
            const saidas = [Sai1, Sai2, Sai3].filter(Boolean)

            return entradas.length === saidas.length
        },
        data => {
            const { Ent1, Ent2, Ent3, Sai1, Sai2, Sai3 } = data

            const entradas = [Ent1, Ent2, Ent3].filter(Boolean)
            const saidas = [Sai1, Sai2, Sai3].filter(Boolean)

            if (entradas.length < saidas.length) {
                return {
                    path: [`Ent${entradas.length + 1}`],
                }
            }

            return {
                path: [`Sai${saidas.length + 1}`],
            }
        },
    )
    .transform(({ label, Tot, ...entSai }) => ({
        label: label || '',
        ...entSai,
        Tot: Tot ?? '00:00',
    }))

const timeInput = z
    .string()
    .nullish()
    .transform(v => v || '00:00')

const toleranciasSchema = z.object({
    entradaAntes: timeInput,
    entradaDepois: timeInput,
    saidaAntes: timeInput,
    saidaDepois: timeInput,
    toleranciaTurno: timeInput.default('00:00'),
    toleranciaDia: timeInput.default('00:00'),
})

const TabelaHorariosSchema = z.object({
    nome: z.string().min(1, 'Descrição é obrigatório'),
    totSem: z.string(),
    horarios: z.array(horariosSchema).min(7),
    codigo: z.coerce.number().nullish(),
    tolerancias: toleranciasSchema,
    diaDescontoDSR: zod.TabelaHorarioSchema.shape.diaDescontoDSR,
    previewEspelho: z.any().optional(),
    tempoPausas: zod.TabelaHorarioSchema.shape.tempoPausas.default('00:00'),
    pausasAcumulam: zod.TabelaHorarioSchema.shape.pausasAcumulam.default('N'),
    maximoPausas: zod.TabelaHorarioSchema.shape.maximoPausas.default(0),
    horasExtraPausas:
        zod.TabelaHorarioSchema.shape.horasExtraPausas.default('N'),
    faltaPausas: zod.TabelaHorarioSchema.shape.faltaPausas.default('N'),
    horasIntervalares:
        zod.TabelaHorarioSchema.shape.horasIntervalares.transform(
            v => v?.trim() || null,
        ),
})

export const CreateTabelaHorariosSchema = TabelaHorariosSchema.transform(
    obj => {
        const horarios = obj.horarios as z.output<typeof horariosSchema>[]
        for (let i = 0; i < horarios.length; i++) {
            horarios[i].label = diasTabelaHorario[i]
        }
        return { ...obj, horarios }
    },
)
export const UpdateTabelaHorariosSchema = TabelaHorariosSchema.extend({
    id: z.number(),
}).transform((obj: z.infer<typeof TabelaHorariosSchema>) => {
    const horarios = obj.horarios as z.output<typeof horariosSchema>[]
    for (let i = 0; i < horarios.length; i++) {
        horarios[i].label = diasTabelaHorario[i]
    }
    return { ...obj, horarios }
})

export const CreateTabelaHorarioFuncionarioSchema = z.object({
    empresaId: z.coerce.number(),
    funcionarioId: z.coerce.number(),
    tabelaHorarioId: z.coerce.number().positive(),
    dtInicial: z.coerce.date(),
    dtFinal: z.coerce
        .string()
        .optional()
        .nullish()
        .transform(v => (v ? new Date(v) : null))
        .refine(
            data => {
                if (!data) return true
                return data.toString() !== 'Invalid Date'
            },
            {
                message: 'Data final inválida',
            },
        ),
})

export const UpdateTabelaHorarioFuncionarioSchema =
    CreateTabelaHorarioFuncionarioSchema.pick({
        dtFinal: true,
        dtInicial: true,
    }).extend({
        id: z.coerce.number(),
    })

export type CreateTabelaHorariosInput = z.input<
    typeof CreateTabelaHorariosSchema
>
export type TabelaHorarios = z.output<typeof UpdateTabelaHorariosSchema> & {
    id: number
    previewEspelho?: PreviewEspelho
    horarios: z.output<typeof horariosSchema>[]
}
export type TabelaHorarioFuncionario = z.infer<
    typeof zod.TabelaHorarioFuncionarioSchema
> & {
    isTrocaAutomatica?: boolean
    TabelaHorario: TabelaHorarios
    Funcionario?: Funcionario
}

const service = new ApiService<CreateTabelaHorariosInput, TabelaHorarios>(
    'tabela-horarios',
)

const funcionario = new ApiService<
    TabelaHorarioFuncionario,
    TabelaHorarioFuncionario,
    {
        funcionarioId: number
        dataInicial: string
        dataFinal: string
    }
>('tabela-horarios/funcionario')

export const tabelaHorarios = Object.assign(service, { funcionario })
